// src/app/people/investigators/page.tsx - Updated with theme number sorting
'use client';

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Skeleton,
  Collapse,
  IconButton,
  Link as MuiLink,
  Divider,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ExpandMore,
  ExpandLess,
  Email,
  Link as LinkIcon,
  Work,
  Psychology,
  Code,
} from '@mui/icons-material';
import Image from 'next/image';
import Link from 'next/link';

// Import API functions
import { api, getMediaUrl } from '@/lib/api';

// Import our modular color system
import { withOpacity } from '@/lib/colors';
import { useColorTheme } from '@/components/ColorModeProvider';
import PageHero from '@/components/PageHero';

// Person interface
interface Person {
  id: number;
  name: string;
  title: string;
  email?: string;
  bio?: string;
  personType: string;
  linkedProfile?: string;
  specialRole?: string;
  affiliation?: {
    name: string;
    website?: string;
  };
  photo?: {
    url: string;
    formats?: {
      thumbnail?: { url: string };
      small?: { url: string };
    };
  } | null;
  researchThemes?: Array<{
    id: number;
    title: string;
    themeNumber: string;
  }>;
  researchAreas?: Array<{
    id: number;
    area: string;
    description?: string;
  }>;
}

// =====================
// UTILITY FUNCTIONS
// =====================

const sortInvestigatorsByTheme = (investigators: Person[]) => {
  return investigators.sort((a, b) => {
    // Get the lowest theme number for each investigator
    const getLowestThemeNumber = (person: Person) => {
      if (!person.researchThemes || person.researchThemes.length === 0) {
        return 999; // Put investigators with no themes at the end
      }
      
      // Extract numeric values from theme numbers (T1 -> 1, T2 -> 2, etc.)
      const themeNumbers = person.researchThemes
        .map(theme => {
          const match = theme.themeNumber.match(/T(\d+)/);
          return match ? parseInt(match[1], 10) : 999;
        })
        .filter(num => num !== 999); // Remove invalid theme numbers
      
      return themeNumbers.length > 0 ? Math.min(...themeNumbers) : 999;
    };
    
    const aTheme = getLowestThemeNumber(a);
    const bTheme = getLowestThemeNumber(b);
    
    // Primary sort: by theme number
    if (aTheme !== bTheme) {
      return aTheme - bTheme;
    }
    
    // Secondary sort: by name if theme numbers are the same
    return a.name.localeCompare(b.name);
  });
};

// =====================
// STYLED COMPONENTS
// =====================

const InvestigatorCard = styled(Card)(({ theme }) => {
  const colors = theme.colors;
  return {
    background: colors.gradients.card,
    border: `1px solid ${colors.border.primary}`,
    borderRadius: '16px',
    marginBottom: '24px',
    transition: 'all 0.3s ease',
    cursor: 'pointer',

    '&:hover': {
      borderColor: colors.border.accent,
      transform: 'translateY(-2px)',
      boxShadow: `0 8px 25px ${colors.effects.glow}`,
    },
  };
});

const MONO_STACK = 'var(--font-jetbrains-mono), ui-monospace, SFMono-Regular, Menlo, monospace';

const monoSectionHeaderSx = {
  fontFamily: MONO_STACK,
  fontSize: '0.78rem',
  fontWeight: 500,
  letterSpacing: '0.04em',
  textTransform: 'lowercase' as const,
  lineHeight: 1,
  mb: 2,
  display: 'inline-block',
};

const InvestigatorAvatar = styled(Avatar)(({ theme }) => {
  const colors = theme.colors;
  return {
    width: 100,
    height: 100,
    border: `3px solid ${withOpacity(colors.brand.accent.main, 0.3)}`,
    fontSize: '2rem',
    fontWeight: 600,
    
    '&:hover': {
      borderColor: colors.border.accent,
    },
  };
});

const RoleChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'role',
})<{ role: string }>(({ theme, role }) => {
  const colors = theme.colors;
  
  const getColors = (roleType: string) => {
    switch (roleType) {
      case 'Lead Investigator': 
        return { 
          bg: withOpacity(colors.brand.accent.main, 0.2), 
          color: colors.brand.accent.dark, 
          border: colors.brand.accent.main 
        };
      case 'Co-Lead': 
        return { 
          bg: withOpacity('#ffa726', 0.2), 
          color: '#ffa726', 
          border: '#ffa726' 
        };
      case 'Co-Investigator': 
        return { 
          bg: withOpacity('#42a5f5', 0.2), 
          color: '#42a5f5', 
          border: '#42a5f5' 
        };
      case 'Theme Lead': 
        return { 
          bg: withOpacity('#ab47bc', 0.2), 
          color: '#ab47bc', 
          border: '#ab47bc' 
        };
      default: 
        return { 
          bg: withOpacity(colors.text.disabled, 0.2), 
          color: colors.text.disabled, 
          border: colors.text.disabled 
        };
    }
  };
  
  const roleColors = getColors(role);
  
  return {
    background: roleColors.bg,
    color: roleColors.color,
    border: `1px solid ${roleColors.border}`,
    fontSize: '0.75rem',
    fontWeight: 500,
  };
});

const SpecialRoleChip = styled(Chip)(({ theme }) => {
  const colors = theme.colors;
  return {
    background: colors.gradients.primary,
    color: colors.brand.accent.contrastText,
    fontWeight: 600,
    fontSize: '0.7rem',
    height: '24px',
  };
});

const ResearchAreaChip = styled(Chip)(({ theme }) => {
  const colors = theme.colors;
  return {
    background: withOpacity(colors.brand.accent.main, 0.1),
    color: colors.text.secondary,
    border: `1px solid ${withOpacity(colors.brand.accent.main, 0.3)}`,
    margin: '2px',
    fontSize: '0.7rem',
    height: '24px',
    
    '&:hover': {
      background: withOpacity(colors.brand.accent.main, 0.2),
      borderColor: colors.border.accent,
    },
  };
});

// =====================
// SUB-COMPONENTS
// =====================

interface InvestigatorItemProps {
  investigator: Person;
  isExpanded: boolean;
  onToggle: () => void;
}

const InvestigatorItem = React.memo<InvestigatorItemProps>(({ investigator, isExpanded, onToggle }) => {
  const colors = useColorTheme();
  
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  return (
    <InvestigatorCard onClick={onToggle}>
      <CardContent sx={{ p: 4 }}>
        {/* Header Section */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 3 }}>
          {/* Avatar */}
          <InvestigatorAvatar>
            {investigator.photo ? (
              <Image
                src={getMediaUrl(investigator.photo)}
                alt={investigator.name}
                unoptimized={true}
                width={100}
                height={100}
                style={{ objectFit: 'cover', borderRadius: '50%' }}
              />
            ) : (
              getInitials(investigator.name)
            )}
          </InvestigatorAvatar>

          {/* Basic Info */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <RoleChip
                icon={<Code/>}
                label={investigator.personType}
                size="small"
                role={investigator.personType}
              />
              {investigator.specialRole && (
                <SpecialRoleChip
                  label={investigator.specialRole}
                  size="small"
                />
              )}
            </Box>

            <Typography
              variant="h5"
              sx={{
                color: colors.brand.accent.dark,
                fontWeight: 700,
                marginBottom: 1,
                lineHeight: 1.2,
              }}
            >
              {investigator.name}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: colors.text.secondary,
                marginBottom: 1,
                fontWeight: 500,
                fontSize: '0.8rem',
              }}
            >
              {investigator.title}
            </Typography>

            {investigator.affiliation && (
              <Typography
                variant="body1"
                sx={{
                  color: colors.text.disabled,
                  display: 'block',
                  fontSize: '0.95rem',
                }}
              >
                {investigator.affiliation.name}
              </Typography>
            )}
          </Box>

          {/* Expand/Collapse Button */}
          <IconButton
            size="small"
            sx={{
              color: colors.brand.accent.dark,
              '&:hover': {
                background: withOpacity(colors.brand.accent.main, 0.1),
              }
            }}
          >
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        {/* Expandable Content */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Divider sx={{ borderColor: withOpacity(colors.brand.accent.main, 0.2), mb: 3 }} />

          {/* Bio */}
          {investigator.bio && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  color: colors.brand.accent.dark,
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Psychology sx={{ fontSize: '1.3rem' }} />
                Biography
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: colors.text.secondary,
                  lineHeight: 1.7,
                  fontSize: '1rem',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {investigator.bio}
              </Typography>
            </Box>
          )}

          {/* Research Areas */}
          {investigator.researchAreas && investigator.researchAreas.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography
                component="div"
                sx={{ ...monoSectionHeaderSx, color: colors.brand.accent.dark }}
              >
                <Box component="span" sx={{ color: withOpacity(colors.brand.accent.dark, 0.55), mr: 0.75 }}>
                  //
                </Box>
                research areas
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {investigator.researchAreas.map((area) => (
                  <ResearchAreaChip
                    key={area.id}
                    label={area.area}
                    size="small"
                    title={area.description}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Research Themes */}
          {investigator.researchThemes && investigator.researchThemes.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography
                component="div"
                sx={{ ...monoSectionHeaderSx, color: colors.brand.accent.dark }}
              >
                <Box component="span" sx={{ color: withOpacity(colors.brand.accent.dark, 0.55), mr: 0.75 }}>
                  //
                </Box>
                research themes
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {investigator.researchThemes.map((theme) => (
                  <Chip
                    key={theme.id}
                    id={`theme-${theme.themeNumber}-${investigator.id}`}
                    label={`${theme.themeNumber}: ${theme.title}`}
                    size="medium"
                    component={Link}
                    href={`/research-themes#theme-${theme.id}`}
                    clickable
                    sx={{
                      background: withOpacity(colors.brand.accent.main, 0.1),
                      color: colors.text.secondary,
                      border: `1px solid ${withOpacity(colors.brand.accent.main, 0.3)}`,
                      fontSize: '0.85rem',
                      height: '32px',
                      textDecoration: 'none',
                      justifyContent: 'flex-start',
                      '&:hover': {
                        background: withOpacity(colors.brand.accent.main, 0.2),
                        borderColor: colors.border.accent,
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Contact Info */}
          <Box sx={{ pt: 3, borderTop: `1px solid ${withOpacity(colors.brand.accent.main, 0.2)}` }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
              {investigator.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email sx={{ fontSize: '1.1rem', color: colors.text.disabled }} />
                  <MuiLink
                    href={`mailto:${investigator.email}`}
                    sx={{
                      color: colors.brand.accent.dark,
                      textDecoration: 'none',
                      fontSize: '0.95rem',
                      '&:hover': {
                        textDecoration: 'underline',
                      }
                    }}
                  >
                    {investigator.email}
                  </MuiLink>
                </Box>
              )}

              {investigator.linkedProfile && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinkIcon sx={{ fontSize: '1.1rem', color: colors.text.disabled }} />
                  <MuiLink
                    href={investigator.linkedProfile}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: colors.brand.accent.dark,
                      textDecoration: 'none',
                      fontSize: '0.95rem',
                      '&:hover': {
                        textDecoration: 'underline',
                      }
                    }}
                  >
                    Academic Profile
                  </MuiLink>
                </Box>
              )}

              {investigator.affiliation?.website && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Work sx={{ fontSize: '1.1rem', color: colors.text.disabled }} />
                  <MuiLink
                    href={investigator.affiliation.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: colors.brand.accent.dark,
                      textDecoration: 'none',
                      fontSize: '0.95rem',
                      '&:hover': {
                        textDecoration: 'underline',
                      }
                    }}
                  >
                    {investigator.affiliation.name}
                  </MuiLink>
                </Box>
              )}
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </InvestigatorCard>
  );
});

InvestigatorItem.displayName = 'InvestigatorItem';

const LoadingSkeleton = React.memo(() => {
  const colors = useColorTheme();
  
  return (
    <>
      {Array.from({ length: 6 }, (_, index) => (
        <Grid key={index} size={{ xs: 12, md: 6 }}>
          <Card sx={{ 
            background: colors.gradients.card, 
            border: `1px solid ${colors.border.primary}`,
            mb: 3,
          }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                <Skeleton 
                  variant="circular" 
                  width={100} 
                  height={100} 
                  sx={{ bgcolor: withOpacity(colors.text.primary, 0.1) }} 
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Skeleton 
                    variant="rectangular" 
                    height={24} 
                    width="40%" 
                    sx={{ bgcolor: withOpacity(colors.text.primary, 0.1), mb: 2 }} 
                  />
                  <Skeleton 
                    variant="text" 
                    height={28} 
                    width="80%" 
                    sx={{ bgcolor: withOpacity(colors.text.primary, 0.15), mb: 1 }} 
                  />
                  <Skeleton 
                    variant="text" 
                    height={20} 
                    width="70%" 
                    sx={{ bgcolor: withOpacity(colors.text.primary, 0.1), mb: 1 }} 
                  />
                  <Skeleton 
                    variant="text" 
                    height={16} 
                    width="60%" 
                    sx={{ bgcolor: withOpacity(colors.text.primary, 0.1) }} 
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </>
  );
});

LoadingSkeleton.displayName = 'LoadingSkeleton';

// =====================
// MAIN COMPONENT
// =====================

const InvestigatorsPage: React.FC = () => {
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [investigators, setInvestigators] = React.useState<Person[]>([]);
  const hasScrolledToTheme = useRef(false);

  const colors = useColorTheme();

  React.useEffect(() => {
    const fetchInvestigators = async () => {
      try {
        setLoading(true);
        
        const investigatorData = await api.getInvestigatorsWithAllData();
        
        console.log('Investigators data:', investigatorData);
        setInvestigators(investigatorData);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch investigators:', err);
        setError(err instanceof Error ? err.message : 'Failed to load investigators');
        setInvestigators([]);
      } finally {
        setLoading(false);
      }
    };

    fetchInvestigators();
  }, []);

  useEffect(() => {
    if (investigators.length > 0 && !hasScrolledToTheme.current) {
      const hash = window.location.hash;
      
      if (hash.match(/^#T\d+$/)) {
        const themeNumber = hash.substring(1);
        
        const investigatorsWithTheme = investigators.filter(inv => 
          inv.researchThemes?.some(theme => theme.themeNumber === themeNumber)
        );
        
        if (investigatorsWithTheme.length > 0) {
          const investigatorIds = investigatorsWithTheme.map(inv => inv.id);
          setExpandedCards(new Set(investigatorIds));
          
          setTimeout(() => {
            const firstInvestigator = investigatorsWithTheme[0];
            const element = document.getElementById(`investigator-${firstInvestigator.id}`);
            if (element) {
              element.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center',
                inline: 'nearest'
              });
              hasScrolledToTheme.current = true;
              
              setTimeout(() => {
                const themeElement = document.getElementById(`theme-${themeNumber}-${firstInvestigator.id}`);
                if (themeElement) {
                  themeElement.style.backgroundColor = withOpacity(colors.brand.accent.main, 0.3);
                  themeElement.style.borderRadius = '8px';
                  themeElement.style.padding = '4px';
                  
                  setTimeout(() => {
                    themeElement.style.backgroundColor = '';
                    themeElement.style.borderRadius = '';
                    themeElement.style.padding = '';
                  }, 3000);
                }
              }, 1000);
            }
          }, 500);
        }
      }
    }
  }, [investigators, colors.brand.accent.main]);

  const handleCardToggle = useCallback((personId: number) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(personId)) {
        newSet.delete(personId);
      } else {
        newSet.add(personId);
      }
      return newSet;
    });
  }, []);

  const stats = useMemo(() => {
    const leadInvestigators = investigators.filter(p => p.personType === 'Lead Investigator').length;
    const coLeads = investigators.filter(p => p.personType === 'Co-Lead').length;
    const otherInvestigators = investigators.filter(p => 
      p.personType === 'Co-Investigator' || p.personType === 'Theme Lead'
    ).length;

    return { leadInvestigators, coLeads, otherInvestigators };
  }, [investigators]);

  return (
    <Box sx={{ minHeight: '100vh', background: colors.background.default }}>
      <PageHero
        label="People"
        title="Program Director and Investigators"
        subtitle="Meet our cybersecurity research leaders driving innovation in emerging technologies"
        icon={<Code />}
      />

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Statistics */}
        <Box sx={{ display: 'flex', gap: 3, mb: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ 
            background: colors.background.elevated,
            border: `1px solid ${colors.border.primary}`,
            p: 3,
            textAlign: 'center',
            minWidth: '140px',
            borderRadius: 2,
          }}>
            <Code sx={{ color: colors.brand.accent.dark, fontSize: '2.5rem', mb: 1 }} />
            <Typography variant="h4" sx={{ color: colors.brand.accent.dark, fontWeight: 700, fontFamily: MONO_STACK, fontVariantNumeric: 'tabular-nums' }}>
              {stats.leadInvestigators}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.disabled }}>
              Lead Investigator
            </Typography>
          </Box>
          
          <Box sx={{ 
            background: colors.background.elevated,
            border: `1px solid ${colors.border.primary}`,
            p: 3,
            textAlign: 'center',
            minWidth: '140px',
            borderRadius: 2,
          }}>
            <Code sx={{ color: colors.brand.accent.dark, fontSize: '2.5rem', mb: 1 }} />
            <Typography variant="h4" sx={{ color: colors.brand.accent.dark, fontWeight: 700, fontFamily: MONO_STACK, fontVariantNumeric: 'tabular-nums' }}>
              {stats.coLeads}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.disabled }}>
              Co-Lead Investigator
            </Typography>
          </Box>

          <Box sx={{ 
            background: colors.background.elevated,
            border: `1px solid ${colors.border.primary}`,
            p: 3,
            textAlign: 'center',
            minWidth: '140px',
            borderRadius: 2,
          }}>
            <Code sx={{ color: colors.brand.accent.dark, fontSize: '2.5rem', mb: 1 }} />
            <Typography variant="h4" sx={{ color: colors.brand.accent.dark, fontWeight: 700, fontFamily: MONO_STACK, fontVariantNumeric: 'tabular-nums' }}>
              {stats.otherInvestigators}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.disabled }}>
              Co-Investigators
            </Typography>
          </Box>
        </Box>

        {/* Content */}
        {loading ? (
          <Grid container spacing={3}>
            <LoadingSkeleton />
          </Grid>
        ) : error ? (
          <Box sx={{ 
            p: 4, 
            textAlign: 'center', 
            background: colors.gradients.card, 
            border: `1px solid ${colors.border.primary}`,
            borderRadius: 2,
          }}>
            <Typography variant="h6" sx={{ color: colors.text.secondary, mb: 2 }}>
              Unable to Load Investigators
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.disabled }}>
              {error}
            </Typography>
          </Box>
        ) : investigators.length > 0 ? (
          <Box>
            {/* Lead Investigators */}
            {investigators.filter(inv => inv.personType === 'Lead Investigator').length > 0 && (
              <Box sx={{ mb: 6 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 3,
                  pb: 2,
                  borderBottom: `2px solid ${colors.brand.accent.main}`,
                }}>
                  <Box sx={{
                    width: 4,
                    height: 40,
                    background: colors.gradients.primary,
                    borderRadius: 2,
                    mr: 2,
                  }} />
                  <Typography
                    variant="h4"
                    sx={{
                      color: colors.brand.accent.dark,
                      fontWeight: 700,
                      fontSize: { xs: '1.5rem', md: '2rem' },
                    }}
                  >
                    Lead Investigator
                  </Typography>
                  <Chip
                    label={investigators.filter(inv => inv.personType === 'Lead Investigator').length}
                    sx={{
                      ml: 2,
                      background: colors.gradients.primary,
                      color: colors.brand.accent.contrastText,
                      fontWeight: 600,
                    }}
                  />
                </Box>
                <Grid container spacing={3}>
                  {sortInvestigatorsByTheme(investigators.filter(inv => inv.personType === 'Lead Investigator'))
                    .map((investigator) => (
                      <Grid key={investigator.id} size={{ xs: 12, md: 6 }}>
                        <div id={`investigator-${investigator.id}`}>
                          <InvestigatorItem
                            investigator={investigator}
                            isExpanded={expandedCards.has(investigator.id)}
                            onToggle={() => handleCardToggle(investigator.id)}
                          />
                        </div>
                      </Grid>
                    ))}
                </Grid>
              </Box>
            )}

            {/* Co-Lead Investigators */}
            {investigators.filter(inv => inv.personType === 'Co-Lead').length > 0 && (
              <Box sx={{ mb: 6 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 3,
                  pb: 2,
                  borderBottom: '2px solid #ffa726',
                }}>
                  <Box sx={{
                    width: 4,
                    height: 40,
                    background: 'linear-gradient(180deg, #ffa726, #ff9800)',
                    borderRadius: 2,
                    mr: 2,
                  }} />
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#ffa726',
                      fontWeight: 700,
                      fontSize: { xs: '1.5rem', md: '2rem' },
                    }}
                  >
                    Co-Lead Investigator
                  </Typography>
                  <Chip
                    label={investigators.filter(inv => inv.personType === 'Co-Lead').length}
                    sx={{
                      ml: 2,
                      background: 'linear-gradient(45deg, #ffa726, #ff9800)',
                      color: '#242424',
                      fontWeight: 600,
                    }}
                  />
                </Box>
                <Grid container spacing={3}>
                  {sortInvestigatorsByTheme(investigators.filter(inv => inv.personType === 'Co-Lead'))
                    .map((investigator) => (
                      <Grid key={investigator.id} size={{ xs: 12, md: 6 }}>
                        <div id={`investigator-${investigator.id}`}>
                          <InvestigatorItem
                            investigator={investigator}
                            isExpanded={expandedCards.has(investigator.id)}
                            onToggle={() => handleCardToggle(investigator.id)}
                          />
                        </div>
                      </Grid>
                    ))}
                </Grid>
              </Box>
            )}

            {/* Other Investigators (Co-Investigators and Theme Leads combined) */}
            {investigators.filter(inv => inv.personType === 'Co-Investigator' || inv.personType === 'Theme Lead').length > 0 && (
              <Box sx={{ mb: 6 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 3,
                  pb: 2,
                  borderBottom: '2px solid #42a5f5',
                }}>
                  <Box sx={{
                    width: 4,
                    height: 40,
                    background: 'linear-gradient(180deg, #42a5f5, #2196f3)',
                    borderRadius: 2,
                    mr: 2,
                  }} />
                  <Typography
                    variant="h4"
                    sx={{
                      color: '#42a5f5',
                      fontWeight: 700,
                      fontSize: { xs: '1.5rem', md: '2rem' },
                    }}
                  >
                    Co-Investigators
                  </Typography>
                  <Chip
                    label={investigators.filter(inv => inv.personType === 'Co-Investigator' || inv.personType === 'Theme Lead').length}
                    sx={{
                      ml: 2,
                      background: 'linear-gradient(45deg, #42a5f5, #2196f3)',
                      color: '#242424',
                      fontWeight: 600,
                    }}
                  />
                </Box>
                
                <Grid container spacing={3}>
                  {sortInvestigatorsByTheme(investigators.filter(inv => inv.personType === 'Co-Investigator' || inv.personType === 'Theme Lead'))
                    .map((investigator) => (
                      <Grid key={investigator.id} size={{ xs: 12, md: 6 }}>
                        <div id={`investigator-${investigator.id}`}>
                          <InvestigatorItem
                            investigator={investigator}
                            isExpanded={expandedCards.has(investigator.id)}
                            onToggle={() => handleCardToggle(investigator.id)}
                          />
                        </div>
                      </Grid>
                    ))}
                </Grid>
              </Box>
            )}

            {/* Organization Chart Summary - Hidden */}
            <Box sx={{ 
              mt: 6,
              p: 4,
              background: colors.background.elevated,
              border: `1px solid ${colors.border.secondary}`,
              borderRadius: 2,
              display: 'none',
            }}>
              <Typography
                variant="h5"
                sx={{
                  color: colors.brand.accent.dark,
                  fontWeight: 600,
                  marginBottom: 3,
                  textAlign: 'center',
                }}
              >
                Research Leadership Structure
              </Typography>
              
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3,
                alignItems: 'center',
                justifyContent: 'space-around',
              }}>
                {/* Lead Investigators */}
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Box sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: colors.gradients.primary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                  }}>
                    <Code sx={{ fontSize: '2rem', color: colors.brand.accent.contrastText }} />
                  </Box>
                  <Typography variant="h6" sx={{ color: colors.brand.accent.dark, fontWeight: 600, mb: 1 }}>
                    Lead Investigators
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.text.disabled, mb: 1 }}>
                    Program Directors & Principal Investigators
                  </Typography>
                  <Chip
                    label={`${stats.leadInvestigators} ${stats.leadInvestigators === 1 ? 'Leader' : 'Leaders'}`}
                    size="small"
                    sx={{
                      background: withOpacity(colors.brand.accent.main, 0.2),
                      color: colors.brand.accent.dark,
                      border: `1px solid ${colors.brand.accent.main}`,
                    }}
                  />
                </Box>

                {/* Hierarchy Arrow */}
                <Box sx={{ 
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  color: colors.text.disabled,
                }}>
                  →
                </Box>

                {/* Co-Lead Investigators */}
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Box sx={{
                    width: 70,
                    height: 70,
                    borderRadius: '50%',
                    background: 'linear-gradient(45deg, #ffa726, #ff9800)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                  }}>
                    <Code sx={{ fontSize: '1.8rem', color: '#242424' }} />
                  </Box>
                  <Typography variant="h6" sx={{ color: '#ffa726', fontWeight: 600, mb: 1 }}>
                    Co-Lead Investigators
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.text.disabled, mb: 1 }}>
                    Associate Directors & Senior Researchers
                  </Typography>
                  <Chip
                    label={`${stats.coLeads} ${stats.coLeads === 1 ? 'Co-Lead' : 'Co-Leads'}`}
                    size="small"
                    sx={{
                      background: 'rgba(255, 167, 38, 0.2)',
                      color: '#ffa726',
                      border: '1px solid #ffa726',
                    }}
                  />
                </Box>

                {/* Hierarchy Arrow */}
                <Box sx={{ 
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  color: colors.text.disabled,
                }}>
                  →
                </Box>

                {/* Other Investigators Combined */}
                <Box sx={{ textAlign: 'center', flex: 1 }}>
                  <Box sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    background: 'linear-gradient(45deg, #42a5f5, #2196f3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                  }}>
                    <Code sx={{ fontSize: '1.6rem', color: '#242424' }} />
                  </Box>
                  <Typography variant="h6" sx={{ color: '#42a5f5', fontWeight: 600, mb: 1 }}>
                    Co-Investigators
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.text.disabled, mb: 1 }}>
                    Research Faculty & Theme Leaders
                  </Typography>
                  <Chip
                    label={`${stats.otherInvestigators} Researchers`}
                    size="small"
                    sx={{
                      background: 'rgba(66, 165, 245, 0.2)',
                      color: '#42a5f5',
                      border: '1px solid #42a5f5',
                    }}
                  />
                </Box>
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: colors.text.disabled,
                  textAlign: 'center',
                  mt: 3,
                  fontStyle: 'italic',
                }}
              >
                Our research leadership spans multiple institutions and brings together diverse expertise 
                in cybersecurity for emerging technologies.
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ 
            p: 4, 
            textAlign: 'center', 
            background: colors.gradients.card, 
            border: `1px solid ${colors.border.primary}`,
            borderRadius: 2,
          }}>
            <Typography variant="h6" sx={{ color: colors.text.secondary, mb: 1 }}>
              No Investigators Found
            </Typography>
            <Typography variant="body2" sx={{ color: colors.text.disabled }}>
              Investigator information will appear here once it&apos;s added to the system.
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default InvestigatorsPage;
